import { FC, useEffect } from 'react';
import { LoadingSpinner } from '@tuic/web-compositions/core/LoadingSpinner';
import { GetSlashCredentialsResponse } from '@lib/third-parties/get-slash.types';
import { GetSlashInternetState } from '../lib/use-get-slash-internet';
import { Locale, TFunction } from '@tuic/i18n';
import { NetworkAccessType } from '@modules/get-slash-internet-card/lib/use-network-access-type';
import type { InternetCardViewData } from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import { DialogViewRenderer } from '@modules/get-slash-internet-card/components/dialog-view-renderer';
import { ErrorView } from '@modules/get-slash-internet-card/components/dialog-views/error-view';
import { CredentialsWarning } from '@modules/get-slash-internet-card/components/credentials-warning';
import { ErrorCode, isErrorCode } from '@lib/errors/error-code';
import { useClientTranslation } from '@lib/i18n/client';
import { createTextRenderer } from '@modules/get-slash-internet-card/lib/utils';
import { PhaserProps } from '@modules/get-slash-internet-card/lib/types';
import { PhaserProvider } from '@modules/get-slash-internet-card/lib/phaser-context';
type Props = {
  locale: Locale;
  viewData: InternetCardViewData | null;
  getSlashCredentials: GetSlashCredentialsResponse;
  getSlashInternet: GetSlashInternetState;
  networkAccessType: NetworkAccessType | undefined;
  useDebugEndpoints: boolean | undefined;
  onTitleChange?: (title: string) => void;
  phaser?: PhaserProps;
};
export const InternetCardContent: FC<Props> = props => {
  const {
    locale,
    viewData,
    getSlashCredentials,
    getSlashInternet,
    networkAccessType,
    useDebugEndpoints,
    onTitleChange,
    phaser
  } = props;
  const {
    t
  } = useClientTranslation();
  const {
    refreshSession
  } = getSlashInternet;
  useEffect(() => {
    refreshSession();
  }, [refreshSession]);
  useEffect(() => {
    if (!onTitleChange) {
      return;
    }
    const title = getTitle({
      t,
      getSlashCredentials,
      viewData,
      networkAccessType,
      session: getSlashInternet.session
    });
    onTitleChange(title);
  }, [t, getSlashCredentials, viewData, networkAccessType, getSlashInternet.session, onTitleChange]);
  const children = renderContent({
    viewData,
    getSlashCredentials,
    locale,
    getSlashInternet,
    networkAccessType,
    useDebugEndpoints
  });
  return <PhaserProvider phaser={phaser} data-sentry-element="PhaserProvider" data-sentry-component="InternetCardContent" data-sentry-source-file="internet-card-content.tsx">{children}</PhaserProvider>;
};
const renderContent = (props: {
  viewData: InternetCardViewData | null;
  getSlashCredentials: GetSlashCredentialsResponse;
  locale: Locale;
  getSlashInternet: GetSlashInternetState;
  networkAccessType: NetworkAccessType | undefined;
  useDebugEndpoints: boolean | undefined;
}) => {
  const {
    viewData,
    getSlashCredentials,
    locale,
    getSlashInternet,
    networkAccessType,
    useDebugEndpoints
  } = props;
  if (getSlashCredentials.error) {
    return <ErrorView error={getSlashCredentials.error} />;
  }
  if (!getSlashInternet.ready) {
    return <div className="py-10 text-center text-marine-40">
        <LoadingSpinner size="lg" className="inline-block" />
      </div>;
  }
  if (viewData === null) {
    return <ErrorView error={ErrorCode.ContentfulDataLoadFailed} />;
  }
  if (getSlashInternet.credentialsValid === false) {
    return <ErrorView error={ErrorCode.GetSlashCredentialsInvalid} />;
  }
  if (!getSlashCredentials.credentials) {
    return <ErrorView error={ErrorCode.GetSlashCredentialsInvalid} />;
  }
  const viewRenderer = <DialogViewRenderer locale={locale} viewData={viewData} getSlashCredentials={getSlashCredentials.credentials} getSlashInternet={getSlashInternet} networkAccessType={networkAccessType} useDebugEndpoints={useDebugEndpoints ?? false} />;
  if (getSlashCredentials.overwritten) {
    return <CredentialsWarning>{viewRenderer}</CredentialsWarning>;
  }
  return viewRenderer;
};
const getTitle = (args: {
  t: TFunction;
  getSlashCredentials: GetSlashCredentialsResponse;
  viewData: InternetCardViewData | null;
  networkAccessType: NetworkAccessType | undefined;
  session: GetSlashInternetState['session'];
}): string => {
  const {
    t,
    getSlashCredentials,
    viewData,
    networkAccessType,
    session
  } = args;
  if (viewData === null || getSlashCredentials?.error) {
    return t('Internet an Bord');
  }
  if (typeof networkAccessType === 'undefined') {
    return t('Laden ...');
  }
  const placeholders = {
    tariff_name: session?.tariffName,
    wifi_name: viewData.shipConfiguration?.wifiName
  };
  const renderText = createTextRenderer(placeholders);
  if (networkAccessType !== NetworkAccessType.Ship) {
    return renderText(viewData.connectView?.title) ?? t('Internet an Bord');
  }
  if (typeof session === 'undefined' || !session.hasBookedTariff) {
    return renderText(viewData.bookingView?.title) ?? t('Internet an Bord');
  }
  return renderText(viewData.consumptionView?.title) ?? t('Internet an Bord');
};